import { useState, useEffect } from 'react';
import Overlay from '../Overlay';
import InlineMessage from '../InlineMessage';
import useMessage from '../../lib/useMessage';
import { supabase } from '../../lib/supabase';
import { getCompanyName } from '../../lib/tenant';
import { toCsv, EXPORT_HARD_CAP } from '../../lib/ledgerExport';
import {
  countVendorsForExport,
  fetchAllVendorsForExport,
  applyVendorSearch,
  buildVendorTable,
  describeVendorExport,
  logVendorExport,
} from '../../lib/vendorCatalogExport';

// Issue #194, Session 78n10. The Vendor Catalog's export control.
//
// Deliberately smaller than the Ledger's sheet, and the difference is the
// point rather than an omission. Gate D2 says one control with the choice
// inside it — the Ledger genuinely has two documents to choose between, and
// this has one. There is one vendor catalog and one shape for it, so a layout
// pill row here would be a question with a single answer.
//
// It keeps the two things D5 and D12 require of every export in this app: the
// row count is stated BEFORE the file is written, and the file says what it is.

export default function VendorCatalogExportSheet({
  isMobile, onClose, statusFilter, search, profile,
}) {
  const [count, setCount] = useState(null);
  const [counting, setCounting] = useState(true);
  const [working, setWorking] = useState(false);
  const [branches, setBranches] = useState([]);
  const msg = useMessage();

  const statusLabel = statusFilter === 'All'
    ? 'Active and inactive'
    : statusFilter === 'Inactive' ? 'Inactive only' : 'Active only';

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setCounting(true);
      try {
        // The branch list is loaded here rather than passed in because the
        // Vendor Catalog page never needed it — branch_id is only meaningful
        // on an internal vendor, which the cards show as a badge rather than
        // as a branch name.
        const [{ count: n }, { data: br }] = await Promise.all([
          countVendorsForExport({ statusFilter }).then(c => ({ count: c })),
          supabase.from('branches').select('id, name'),
        ]);
        if (cancelled) return;
        setCount(n);
        setBranches(br || []);
      } catch (e) {
        if (!cancelled) msg.showError('Could not count the matching vendors: ' + (e.message || e));
      } finally {
        if (!cancelled) setCounting(false);
      }
    })();
    return () => { cancelled = true; };
    // The filters do not change while this is open.
  }, []); // eslint-disable-line

  const tooLarge = count != null && count > EXPORT_HARD_CAP;

  function branchName(id) {
    return branches.find(b => String(b.id) === String(id))?.name || '';
  }

  async function doExport() {
    setWorking(true);
    msg.clearMessage();
    try {
      const raw = await fetchAllVendorsForExport({ statusFilter }, count);
      const rows = applyVendorSearch(raw, search);
      const table = buildVendorTable(rows, { branchName });

      const block = describeVendorExport({
        companyName: getCompanyName(),
        statusLabel,
        search,
        // The count the FILE states is the count actually written, not the
        // pre-search estimate this dialog showed. Those differ whenever a
        // search is active.
        rowCount: rows.length,
        generatedBy: profile?.full_name || 'Unknown',
        generatedAt: new Date().toLocaleString('en-US'),
      });

      const blob = new Blob([toCsv(block, table.headers, table.body)], {
        type: 'text/csv;charset=utf-8;',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `vendors_${statusFilter.toLowerCase()}_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);

      await logVendorExport({
        profile,
        filters: { status: statusLabel, search: search || null },
        rowCount: rows.length,
      });

      onClose();
    } catch (e) {
      msg.showError('The export could not be completed: ' + (e.message || e));
      setWorking(false);
    }
  }

  return (
    <Overlay variant={isMobile ? 'sheet' : 'modal'} onClose={working ? undefined : onClose} maxWidth={520}>
      <div className="modal-header">
        <span className="modal-title">Export vendors</span>
        <button className="close-btn" onClick={onClose} disabled={working}>×</button>
      </div>

      <div className="modal-body">
        <InlineMessage type={msg.type} message={msg.message} onDismiss={msg.clearMessage} />

        <div className="text-muted" style={{ fontSize: 13, marginBottom: 16 }}>
          {statusLabel}{search ? ` · matching “${search}”` : ''}
        </div>

        {/* Gate callout E — the count is stated before the file is made. */}
        <div style={{
          padding: '11px 13px', borderRadius: 8, fontSize: 13, lineHeight: 1.5,
          background: tooLarge ? '#FDF3F3' : 'var(--off-white, #F5F5F3)',
          border: `1px solid ${tooLarge ? '#F0CFCF' : 'var(--gray-ln, #E3E3DF)'}`,
          marginBottom: 14,
        }}>
          {counting ? 'Counting what matches…'
            : count == null ? 'The matching vendors could not be counted.'
            : tooLarge ? (
              <>
                <strong>{count.toLocaleString()} vendors match</strong> — more than one file can
                carry. Narrow the filter, then export again.
              </>
            ) : (
              <>
                <strong>{count.toLocaleString()}</strong> {count === 1 ? 'vendor matches' : 'vendors match'}.
                {search
                  ? ' The search box narrows the file further, and the number written is stated inside it.'
                  : ` All ${count.toLocaleString()} will be included.`}
              </>
            )}
        </div>

        {/* The round trip, said plainly. This is the half of #194 that makes
            the file worth having: it is not a report, it is the thing you edit
            and hand back. */}
        <div style={{
          padding: '11px 13px', borderRadius: 8, fontSize: 12.5, lineHeight: 1.5,
          border: '1px solid var(--gray-ln, #E3E3DF)',
        }}>
          <strong>This file is also the import format.</strong> Edit it in Excel and import it
          back from this page. Each row keeps its Stede ID, so an edited row updates that vendor
          instead of creating a second one — clear the ID cell to add a new vendor.
          <div className="text-muted" style={{ marginTop: 6 }}>
            Format the ZIP and Phone columns as Text before editing. Excel drops the leading zero
            from 01234 and writes 1234 back.
          </div>
        </div>
      </div>

      <div className="modal-footer">
        <button className="btn btn-ghost" onClick={onClose} disabled={working}>Cancel</button>
        {/* Gate callout G — blocked, not refused after the click. */}
        <button
          className="btn btn-primary"
          onClick={doExport}
          disabled={working || counting || tooLarge || count == null || count === 0}
        >
          {working ? 'Exporting…' : 'Export CSV'}
        </button>
      </div>
    </Overlay>
  );
}
